import * as numeral from 'numeral';

import { Component, Input, OnDestroy, OnInit } from '@angular/core';

import { EmitterService } from '../../../../core/utility/services/emitter.service';
import { UtilityService } from '../../../../core/utility/services/utility.service';
import { DownloadConfig } from '../../../analytics-core/interfaces/widget.interface';
import { ColorUtilityService } from '../../../analytics-core/services/color.utility.service';
import { EVENT_EMITTERS, FILE_MEDIA_TYPES, PATHS } from '../../../analytics/constants/analytics.constants';
import { AnalyticsDataModel } from '../../../analytics/models/analytics.datamodel';
import { AnalyticsService } from '../../../analytics/services/analytics.service';
import { ModalService } from '../../../shared/directives/modal/modal.service';
import { WidgetCore } from '../../interfaces/widget.core.interface';
import { CreativeAdSize, CreativeDetail, VideoCompletionPercentage } from './creative-detail.interface';

// tslint:disable-next-line: interface-name
interface IMediaFile {
	type: string;
	url: string;
	width: number;
	height: number;
}

@Component({
	selector: 'app-creative-detail-v2',
	styleUrls: ['./creative-detail-v2.component.css'],
	templateUrl: './creative-detail-v2.component.html'
})
export class CreativeDetailV2Component implements OnInit, WidgetCore, OnDestroy {
	@Input() downloadconfig: string;
	@Input() downloadcsvcolumns: any;
	@Input() sectionclass: string;

	public selectedCreative: CreativeDetail;
	public downloadConfiguration: DownloadConfig;
	public sectiontitle: string;
	public key: string;
	public colors: string;
	public showLoad = false;
	public placeholderimage;
	public hasData: boolean;
	public creatives: CreativeDetail[] = [];
	public partnerColors: string[] = [];
	public reloadSubscription;
	public widgetDataSubscription;
	public updateIndexSubscription: any;
	public isRadio: boolean;
	public isSocial: boolean;
	public downloadCSVColumns: any;
	public youtubeViewsPermission: boolean = false;
	public youtubeViewsPermissionCheck: object = { permission: { flags: ['hasYoutubeViews'] } };

	constructor(
		protected analyticsService: AnalyticsService,
		protected colorUtilityService: ColorUtilityService,
		protected modalService: ModalService,
		protected analyticsDataModel: AnalyticsDataModel
	) {
		this.reloadSubscription = EmitterService.get(EVENT_EMITTERS.RELOAD).subscribe((response: any) => {
			this.requestData();
		});
		this.updateIndexSubscription = EmitterService.get('UPDATE_INDEX').subscribe((index) => {
			this.updateVideoData(index);
			if (this.isRadio) {
				EmitterService.get('PLAY_SELECTED_RADIO_CREATIVE').emit(this.creatives[index]);
			}
		});
		this.isRadio = this.analyticsDataModel.getCurrentPath() === PATHS.RADIO;
		this.isSocial = this.analyticsDataModel.getCurrentPath() === PATHS.SOCIAL;
	}

	ngOnInit(): void {
		this.youtubeViewsPermission = this.analyticsDataModel.checkComponentForPermission(this.youtubeViewsPermissionCheck);
		this.downloadConfiguration = JSON.parse(decodeURI(this.downloadconfig));
		this.partnerColors = this.colors.split(',');
		this.downloadCSVColumns = this.downloadcsvcolumns ? JSON.parse(decodeURI(this.downloadcsvcolumns)) : [];
		this.requestData();
	}

	requestData(): void {
		this.hasData = true;
		this.showLoad = true;
		if (!this.isRadio) {
			this.widgetDataSubscription = this.analyticsService
				.processCustomServiceRequest(this.key)
				.subscribe((response: any) => {
					this.creatives = [];
					if (response.success) {
						this.hasData= response.data.length != 0 ? true : false;
						this.processResponseData(response.data);
					} else {
						this.hasData = false;
					}
					this.showLoad = false;
				});
		} else {
			this.widgetDataSubscription = this.analyticsService.processWidgetRequest(this.key).subscribe((response: any) => {
				this.creatives = [];
				if (response.success) {
					this.hasData= response.data.length != 0 ? true : false;
					this.processResponseData(response.data);
				} else {
					this.hasData = false;
				}
				this.showLoad = false;
			});
		}
	}

	processResponseData(responseData: any): void {
		this.mapResponseToInterface(responseData);
	}

	updateVideoData(index: any): void {
		EmitterService.get('PLAY_SELECTED_VIDEO').emit(this.creatives[index]);
	}

	mapResponseToInterface(responseData: any): void {
		let counter = 0;
		for (const responseObj of responseData) {
			const creativeObj: CreativeDetail = {} as CreativeDetail;
			creativeObj.adInteractions = UtilityService.isUndefinedOrNull(responseObj.ad_interactions)
				? 0
				: parseInt(responseObj.ad_interactions, 10);
			creativeObj.adInteractionRate = UtilityService.isUndefinedOrNull(responseObj.interaction_rate)
				? 0
				: parseFloat(responseObj.interaction_rate).toFixed(2);
			creativeObj.impressions = UtilityService.isUndefinedOrNull(responseObj.impressions)
				? 0
				: parseInt(responseObj.impressions, 10);
			creativeObj.clickRate = UtilityService.isUndefinedOrNull(responseObj.click_rate)
				? 0
				: parseFloat(responseObj.click_rate).toFixed(2);
			creativeObj.clicks = UtilityService.isUndefinedOrNull(responseObj.clicks) ? 0 : parseInt(responseObj.clicks, 10);
			creativeObj.versionId = UtilityService.isUndefinedOrNull(responseObj.cup_version_id)
				? 0
				: parseInt(responseObj.cup_version_id, 10);
			creativeObj.creativeName = responseObj.name;
			creativeObj.isIframeExpired = responseObj.is_iframe_expired;
			creativeObj.snapshotImage = responseObj.snapshot_path;
			creativeObj.hasSnapshotImage = !UtilityService.isUndefinedOrNull(responseObj.snapshot_path);
			creativeObj.iframeUrl = UtilityService.isUndefinedOrNull(responseObj.iframe_url) ? null : responseObj.iframe_url;
			creativeObj.creativeThumbnail = UtilityService.isUndefinedOrNull(responseObj.thumbnail)
				? this.placeholderimage
				: responseObj.thumbnail;
			creativeObj.htmlClass = this.getClassByIndex(counter);
			creativeObj.isVideoCreative =
				!UtilityService.isUndefinedOrNull(responseObj.mp4) || responseObj.is_video_creative == 'video' ? true : false;

			creativeObj.adSizes = !creativeObj.isVideoCreative
				? this.mapResponseToAdSizes(responseObj.creatives_by_ads ? responseObj.creatives_by_ads : [])
				: ({} as { string: CreativeAdSize });
			creativeObj.hasAdSizes = !creativeObj.isVideoCreative ? !!responseObj.creatives_by_ads : false;
			creativeObj.isIAB = UtilityService.isUndefinedOrNull(responseObj.isIAB)
				? false
				: Number(responseObj.isIAB)
				? true
				: false;
			creativeObj.isNative = UtilityService.isUndefinedOrNull(responseObj.isNative)
				? false
				: Number(responseObj.isNative)
				? true
				: false;
			creativeObj.native = responseObj.native;
			if (creativeObj.isVideoCreative) {
				creativeObj.creativeVideo = responseObj.mp4;
				if (responseObj.mp4 === null || responseObj.mp4 === '') {
					creativeObj.creativeVideo = this.getMp4FromMediaFilesJson(responseObj.media_files);
				}
				if (creativeObj.creativeVideo === null && responseObj.video_url !== '') {
					creativeObj.creativeVideo = responseObj.video_url;
				}
				creativeObj.completionRate = UtilityService.isUndefinedOrNull(responseObj['100_percent_rate'])
					? 0
					: parseFloat(responseObj['100_percent_rate']).toFixed(2);
				creativeObj.completions = UtilityService.isUndefinedOrNull(responseObj['100_percent_value'])
					? 0
					: parseInt(responseObj['100_percent_value'], 10);

				let videoCompletionPercentage: VideoCompletionPercentage = {} as VideoCompletionPercentage;
				videoCompletionPercentage.started = 100;
				videoCompletionPercentage.completed = UtilityService.isUndefinedOrNull(responseObj['100_percent_rate'])
					? 0
					: parseFloat(responseObj['100_percent_rate']).toFixed(2);
				videoCompletionPercentage.completionValue = UtilityService.isUndefinedOrNull(responseObj['100_percent_value'])
					? 0
					: parseInt(responseObj['100_percent_value'], 10);
				videoCompletionPercentage.quarter = UtilityService.isUndefinedOrNull(responseObj['25_percent_rate'])
					? 0
					: parseFloat(responseObj['25_percent_rate']).toFixed(2);
				videoCompletionPercentage.quarterValue = UtilityService.isUndefinedOrNull(responseObj['25_percent_value'])
					? 0
					: parseInt(responseObj['25_percent_value'], 10);
				videoCompletionPercentage.halfValue = UtilityService.isUndefinedOrNull(responseObj['50_percent_value'])
					? 0
					: parseInt(responseObj['50_percent_value'], 10);
				videoCompletionPercentage.half = UtilityService.isUndefinedOrNull(responseObj['50_percent_rate'])
					? 0
					: parseFloat(responseObj['50_percent_rate']).toFixed(2);
				videoCompletionPercentage.threeQuarters = UtilityService.isUndefinedOrNull(responseObj['75_percent_rate'])
					? 0
					: parseFloat(responseObj['75_percent_rate']).toFixed(2);
				videoCompletionPercentage.threeQuartersValue = UtilityService.isUndefinedOrNull(responseObj['75_percent_value'])
					? 0
					: parseInt(responseObj['75_percent_value'], 10);
				videoCompletionPercentage = this.addVideoCompletionTooltip(videoCompletionPercentage);
				creativeObj.videoCompletionPercentage = videoCompletionPercentage;
				creativeObj.youtubeVerifiedImprs = UtilityService.isUndefinedOrNull(responseObj.youtube_verified_imprs)
					? 0
					: parseInt(responseObj.youtube_verified_imprs, 10);
				creativeObj.youtubeVerifiedImprsRate = UtilityService.isUndefinedOrNull(responseObj.youtube_verified_imprs_rate)
					? 0
					: parseFloat(responseObj.youtube_verified_imprs_rate).toFixed(2);
			}
			creativeObj.engagements = UtilityService.isUndefinedOrNull(responseObj.engagements)
				? 0
				: parseInt(responseObj.engagements, 10);
			creativeObj.likes = UtilityService.isUndefinedOrNull(responseObj.likes) ? 0 : parseInt(responseObj.likes, 10);
			creativeObj.shares = UtilityService.isUndefinedOrNull(responseObj.shares) ? 0 : parseInt(responseObj.shares, 10);
			creativeObj.saves = UtilityService.isUndefinedOrNull(responseObj.saves) ? 0 : parseInt(responseObj.saves, 10);
			creativeObj.comments = UtilityService.isUndefinedOrNull(responseObj.comments)
				? 0
				: parseInt(responseObj.comments, 10);
			creativeObj.reactions = UtilityService.isUndefinedOrNull(responseObj.reactions)
				? 0
				: parseInt(responseObj.reactions, 10);
			this.creatives.push(creativeObj);
			counter++;
		}
	}

	getClassByIndex(index: any): string {
		const colorIndex = index % this.partnerColors.length;
		return this.partnerColors[colorIndex].trim() + '-color-bg';
	}

	getMp4FromMediaFilesJson(mediaFilesJson: string): string {
		let mp4Link: string = null;

		let decodedMediaFiles: IMediaFile[];
		let isJsonDecodeSuccess = false;
		try {
			decodedMediaFiles = JSON.parse(mediaFilesJson);
			isJsonDecodeSuccess = true;
		} catch (e) {
			isJsonDecodeSuccess = false;
		}

		if (isJsonDecodeSuccess) {
			let fallBackMediaFile: IMediaFile;
			for (const mediaFile of decodedMediaFiles) {
				if (mediaFile.type === FILE_MEDIA_TYPES.MP4) {
					if (mediaFile.width === 640 && mediaFile.height === 360) {
						mp4Link = mediaFile.url;
						break;
					} else {
						if (fallBackMediaFile) {
							if (fallBackMediaFile.width > mediaFile.width && mediaFile.width >= 640) {
								fallBackMediaFile = mediaFile;
							}
						} else {
							fallBackMediaFile = mediaFile;
						}
					}
				}
			}

			if ((mp4Link === null || mp4Link === '') && fallBackMediaFile) {
				mp4Link = fallBackMediaFile.url;
			}
		}

		return mp4Link;
	}

	ngOnDestroy(): void {
		if (this.reloadSubscription) {
			this.reloadSubscription.unsubscribe();
		}
		if (this.widgetDataSubscription) {
			this.widgetDataSubscription.unsubscribe();
		}
		if (this.updateIndexSubscription) {
			this.updateIndexSubscription.unsubscribe();
		}
		this.selectedCreative = undefined;
	}

	downloadDataInCSV(event: any): void {
		event.preventDefault();
		this.analyticsService.downloadCSV(
			this.downloadConfiguration.csvQueryKey,
			this.downloadConfiguration.fileName,
			this.downloadCSVColumns
		);
	}

	previewCreative(creativeIndex: number): void {
		this.selectedCreative = this.creatives[creativeIndex];
		this.modalService.open('creative-preview-v2');
	}

	closeModal(modalId: string): void {
		this.modalService.close(modalId);
		this.selectedCreative = undefined;
	}

	protected addVideoCompletionTooltip(videoCompletionPercentage: VideoCompletionPercentage): VideoCompletionPercentage {
		videoCompletionPercentage.startedTooltip = this.addVideoCompletionTooltipHTML(
			undefined,
			videoCompletionPercentage.started
		);
		videoCompletionPercentage.completedTooltip = this.addVideoCompletionTooltipHTML(
			videoCompletionPercentage.completionValue,
			videoCompletionPercentage.completed
		);
		videoCompletionPercentage.quarterTooltip = this.addVideoCompletionTooltipHTML(
			videoCompletionPercentage.quarterValue,
			videoCompletionPercentage.quarter
		);
		videoCompletionPercentage.halfTooltip = this.addVideoCompletionTooltipHTML(
			videoCompletionPercentage.halfValue,
			videoCompletionPercentage.half
		);
		videoCompletionPercentage.threeQuartersTooltip = this.addVideoCompletionTooltipHTML(
			videoCompletionPercentage.threeQuartersValue,
			videoCompletionPercentage.threeQuarters
		);
		return videoCompletionPercentage;
	}

	protected addVideoCompletionTooltipHTML(completions: any, completionRate: any): string {
		let tooltipHTML = '';
		if (completions) {
			tooltipHTML += `<div class="display-text">${numeral(completions).format('0,0')} views </div>`;
		}
		if (completionRate) {
			tooltipHTML += `<div class="display-text" style="font-size: 1.3em">${completionRate}% </div>`;
		}
		return tooltipHTML;
	}

	protected mapResponseToAdSizes(responseData: any): { string: CreativeAdSize } {
		const creativeAdSizeObj: { string: CreativeAdSize } = {} as { string: CreativeAdSize };
		for (const response of responseData) {
			const adSizeObj: CreativeAdSize = {} as CreativeAdSize;
			adSizeObj.adSize = response.ad_size;
			adSizeObj.creativeThumbnail = response.creative_image;
			adSizeObj.cupVersionId = response.cup_version_id;
			creativeAdSizeObj[adSizeObj.adSize] = adSizeObj;
		}
		return creativeAdSizeObj;
	}
}
